import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronLeft, Eye, Printer } from 'lucide-react';
import { api } from '../services/api';
import { trackProductEvent } from '../services/tracking';
import QuantityControl from '../components/QuantityControl';
import LoginModal from '../components/LoginModal';
import { usePreinvoice } from '../context/PreinvoiceContext';
import { useToast } from '../context/ToastContext';
import { useWebAuth } from '../context/WebAuthContext';
import { isWeightKey } from '../utils/weight';
import SpecValue from '../components/SpecValue';
import BrandCard from '../components/BrandCard';

function cleanCell(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/<[^>]*>/g, '').trim();
}

function getProductSpecs(product) {
  const specs = product?.tech_data?.specs || product?.specs || product?.tech_data || {};
  if (!specs || typeof specs !== 'object' || Array.isArray(specs)) return {};
  return specs;
}

function isHiddenSpecKey(key) {
  const normalized = String(key || '').trim().toLowerCase();
  return [
    'image_url',
    'image url',
    'product_image',
    'show_product_link',
    'show product link',
    'can_view_product',
    'show_product',
    'weight',
    'weight_kg',
    'unit_weight',
    'وزن',
  ].includes(normalized);
}

function getSpecKeys(rows) {
  const keys = [];
  const seen = new Set();
  rows.forEach((row) => {
    Object.entries(getProductSpecs(row)).forEach(([key, value]) => {
      if (isWeightKey(key) || isHiddenSpecKey(key) || value === null || value === undefined || String(value) === '' || seen.has(key)) return;
      seen.add(key);
      keys.push(key);
    });
  });
  return keys;
}

function getBrandText(product) {
  if (Array.isArray(product?.brands) && product.brands.length) {
    return product.brands.map((b) => b?.name || b?.title).filter(Boolean).join('، ');
  }
  return product?.brand_title || product?.brandTitle || product?.brand_name || product?.brandName || '';
}

function canShowProduct(product) {
  return Boolean(
    product?.can_view_product ||
    product?.show_product_link ||
    product?.show_product ||
    product?.is_publicly_viewable ||
    product?.tech_data?.show_product_link
  );
}

function buildRowsTableHtml(rows, title = 'products') {
  const safeRows = Array.isArray(rows) ? rows : [];
  const specKeys = getSpecKeys(safeRows);
  const columns = [
    ['row', 'ردیف'],
    ['title', 'شرح کالا'],
    ['brand', 'برند'],
    ['unit', 'واحد'],
    ['price', 'قیمت'],
    ...specKeys.map((key) => [`spec:${key}`, key]),
  ];
  const cell = (value) => `<td>${cleanCell(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
  const rowsHtml = safeRows.map((row, index) => {
    const specs = getProductSpecs(row);
    return `<tr>${columns.map(([key]) => {
      if (key === 'row') return cell(index + 1);
      if (key === 'title') return cell(row.title || row.name || '');
      if (key === 'brand') return cell(getBrandText(row));
      if (key === 'unit') return cell(row.unit || '');
      if (key === 'price') return cell(row.price ? Number(row.price).toLocaleString('fa-IR') : 'استعلام');
      if (key.startsWith('spec:')) return cell(specs[key.slice(5)] || '');
      return cell('');
    }).join('')}</tr>`;
  }).join('');

  return `<!doctype html>
    <html lang="fa" dir="rtl">
      <head>
        <meta charset="utf-8" />
        <title>${cleanCell(title)}</title>
        <style>
          body { font-family: Tahoma, Arial, sans-serif; margin: 24px; color: #111827; }
          h1 { font-size: 18px; margin: 0 0 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #d1d5db; padding: 8px; text-align: center; }
          th { background: #eef6ff; font-weight: 700; }
          tr:nth-child(even) td { background: #fafafa; }
          @page { size: landscape; margin: 12mm; }
        </style>
        <script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 300); });</script>
      </head>
      <body>
        <h1>${cleanCell(title)}</h1>
        <table>
          <thead><tr>${columns.map(([, label]) => `<th>${label}</th>`).join('')}</tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body>
    </html>`;
}

function printRowsAsPdf(rows, title = 'products') {
  const html = buildRowsTableHtml(rows, title);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank', 'width=1200,height=800');
  if (!printWindow) {
    URL.revokeObjectURL(url);
    return;
  }
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function BrandRowsTable({ rows, title, brand, isAuthed, setLoginOpen, addItem, showToast }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const specKeys = getSpecKeys(safeRows);
  const [rowQuantities, setRowQuantities] = useState({});

  const getRowQuantity = (product) => rowQuantities[product.id] ?? 1;
  const setRowQuantity = (productId, next) => setRowQuantities((prev) => ({ ...prev, [productId]: next }));

  const addProduct = (product) => {
    const quantity = Math.max(Number(getRowQuantity(product)) || 1, 1);
    if (!isAuthed) {
      setLoginOpen(true);
      return;
    }

    addItem({
      ...product,
      selected_brand_id: brand?.id || product.brand_id || null,
      selected_brand_name: brand?.name || brand?.title || getBrandText(product) || null,
      product_code: product.product_code || product.sku || product.external_code || product.barcode,
    }, quantity);
    trackProductEvent('add_to_preinvoice', {
      product_item_id: product.id || product.product_item_id || null,
      brand_id: brand?.id || product.brand_id || null,
      category_id: product.category_id || null,
      category_slug: product.category_slug || null,
      source: 'brand_page_table',
      metadata: { title: product.title || '', sku: product.sku || product.product_code || '', quantity },
    });
    showToast({
      title: 'به پیش‌فاکتور اضافه شد',
      message: `${product.title || product.name || 'محصول'} به سبد پیش‌فاکتور شما اضافه شد.`,
      type: 'success',
    });
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-gray-100 bg-blue-50/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="break-words text-sm font-extrabold text-slate-900">{title}</h3>
          <p className="mt-1 text-xs text-gray-500">{safeRows.length.toLocaleString('fa-IR')} ردیف محصول</p>
        </div>
        <button
          type="button"
          onClick={() => printRowsAsPdf(safeRows, title)}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-extrabold text-blue-700 transition hover:bg-blue-50"
        >
          <Printer className="h-4 w-4" />
          چاپ
        </button>
      </div>

      <div className="overflow-auto">
        <table className="w-full min-w-[920px] text-center text-xs sm:text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3 text-center">ردیف</th>
              <th className="px-4 py-3 text-center">شرح کالا</th>
              <th className="px-4 py-3 text-center">برند</th>
              <th className="px-4 py-3 text-center">واحد</th>
              <th className="px-4 py-3 text-center">قیمت</th>
              {specKeys.map((key) => (
                <th key={key} className="px-4 py-3 text-center">{key}</th>
              ))}
              <th className="px-4 py-3 text-center">عملیات</th>
            </tr>
          </thead>
          <tbody>
            {safeRows.map((product, index) => {
              const specs = getProductSpecs(product);
              return (
                <tr key={product.id || product.sku || product.title} className="border-t border-gray-100 transition hover:bg-slate-100/70">
                  <td className="px-4 py-3 text-gray-500">{(index + 1).toLocaleString('fa-IR')}</td>
                  <td className="px-4 py-3 font-bold text-slate-900">{product.title || product.name || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{brand?.name || brand?.title || getBrandText(product) || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{product.unit || '-'}</td>
                  <td className="px-4 py-3 font-bold">
                    {product.price ? (
                      <span className="text-emerald-700">{Number(product.price).toLocaleString('fa-IR')}</span>
                    ) : (
                      <span title="برای استعلام تماس بگیرید" className="cursor-help text-[#005bbb]">استعلام</span>
                    )}
                  </td>
                  {specKeys.map((key) => (
                    <td key={key} className="px-4 py-3 text-gray-700"><SpecValue name={key} value={specs[key] ?? '-'} /></td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <QuantityControl
                        value={getRowQuantity(product)}
                        onChange={(next) => setRowQuantity(product.id, next)}
                        className="h-8 rounded-full"
                        inputClassName="h-8 w-11 text-xs"
                        buttonClassName="h-6 w-6"
                      />
                      {canShowProduct(product) ? (
                        <Link to={`/product/${product.id}`} className="inline-flex h-8 items-center gap-1 rounded-full border border-gray-200 px-3 text-xs font-bold text-blue-700 hover:bg-blue-50">
                          <Eye className="h-3.5 w-3.5" />
                          نمایش محصول
                        </Link>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => addProduct(product)}
                        className="h-8 rounded-full bg-[#005bbb] px-3 text-xs font-bold text-white hover:bg-[#004a99]"
                      >
                        افزودن
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!safeRows.length && (
              <tr>
                <td className="px-4 py-8 text-center text-gray-500" colSpan={6 + specKeys.length}>
                  ردیفی برای این جدول ثبت نشده است.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BrandPage() {
  const { slug } = useParams();
  const { addItem } = usePreinvoice();
  const { showToast } = useToast();
  const { isAuthed } = useWebAuth();
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const br = await api.getBrandBySlug(slug);
        const b = br?.data || br;
        const pr = await api.getProducts({ brand: slug, limit: 200 });
        if (!mounted) return;
        setBrand(b || null);
        setProducts(pr?.data || []);
      } catch {
        if (!mounted) return;
        setBrand(null);
        setProducts([]);
      } finally {
        mounted && setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const title = useMemo(() => brand?.name || brand?.title || (loading ? 'در حال بارگذاری...' : 'برند'), [brand, loading]);

  const groups = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      const key = p.category_slug || 'other';
      if (!map.has(key)) {
        map.set(key, {
          slug: p.category_slug,
          title: p.category_title || 'محصولات',
          rows: [],
        });
      }
      map.get(key).rows.push(p);
    });
    return Array.from(map.values());
  }, [products]);

  useEffect(() => {
    const totalRows = groups.reduce((sum, group) => sum + group.rows.length, 0);
    const shouldOpenSingleGroup = groups.length === 1 && totalRows <= 1;
    setExpandedGroups(() => {
      const next = {};
      groups.forEach((group) => {
        const key = group.slug || group.title;
        next[key] = shouldOpenSingleGroup;
      });
      return next;
    });
  }, [groups]);

  return (
    <main className="min-h-[70vh] bg-gray-50" dir="rtl">
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
      <section className="border-b border-gray-200 bg-white">
        <div className="pp-container py-8">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Link to="/" className="font-bold text-[#005bbb] hover:underline">پارس پایپینگ</Link>
            <ChevronLeft className="h-3 w-3" />
            <span>برندها</span>
            <ChevronLeft className="h-3 w-3" />
            <span className="font-bold text-slate-700">{title}</span>
          </div>
          <div className="mt-4 flex items-center gap-3">
            {brand && <div className="w-28 shrink-0"><BrandCard brand={brand} /></div>}
            <h1 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">{title}</h1>
          </div>
          <p className="mt-2 text-sm leading-7 text-gray-600">
            دسته‌بندی‌های دارای محصول برای این برند را مشاهده کنید.
          </p>
        </div>
      </section>

      <section className="pp-container py-10">
        {!loading && !brand && (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
            برند موردنظر پیدا نشد.
          </div>
        )}

        {groups.length === 0 ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
            {loading ? 'در حال بارگذاری...' : 'محصولی برای این برند ثبت نشده است.'}
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((group) => {
              const groupKey = group.slug || group.title;
              const isOpen = !!expandedGroups[groupKey];
              return (
                <section key={groupKey} className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
                  <button
                    type="button"
                    onClick={() => setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                    className="flex w-full flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-white px-5 py-4 text-right transition hover:bg-gray-50"
                  >
                    <div>
                      <h2 className="text-base font-extrabold text-slate-900">{group.title}</h2>
                      <div className="mt-1 text-xs text-gray-500">{group.rows.length.toLocaleString('fa-IR')} ردیف محصول مرتبط</div>
                    </div>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-blue-700 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen ? (
                    <div className="border-t border-gray-100 p-4">
                      <BrandRowsTable
                        rows={group.rows}
                        title={group.title}
                        brand={brand}
                        isAuthed={isAuthed}
                        setLoginOpen={setLoginOpen}
                        addItem={addItem}
                        showToast={showToast}
                      />
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
